import { defineStore } from 'pinia'
import { ref } from 'vue'
import * as sdk from '@/helpers/sdk'

import profileQueue from '@/helpers/profileQueue'
import useRouterStore from '@/stores/router.store'
import useProfileStore, { profileKey } from '@/stores/profile.store'
import routeNames from '@/router/names'
import getRedirectAfterDelete from '@/helpers/profile/getRedirectAfterDelete'
import useAction from '@/composables/useAction'
import linkToQuery from '@/helpers/profile/linkToQuery'

const meta = {
  requestPermission: {
    read: true,
    edit: true,
  },
}

async function createMode({
  query = false,
  redirect = true,
  ...payload
} = {}) {
  const profileStore = useProfileStore()
  const routerStore = useRouterStore()

  const profile = profileStore[profileKey]

  const {
    modes,
  } = profileStore // current

  let newOrder = 0
  if (typeof payload._order === 'number') {
    newOrder = payload._order
  } else if (modes) {
    newOrder = modes.length
  }

  const result = await profileQueue.add(
    () => sdk.v3.profiles.modes.create({
      url: {
        profileUid: profile.uid,
      },
      body: payload,
    }),
  ).result

  profileStore[profileKey] = result.data

  // Redirect based on index, to select the newly created mode
  if (redirect) {
    const newModeLink = {
      name: routeNames.Mode,
      params: {
        profileUid: profileStore.profileUid,
        modeUid: newOrder,
      },
    }
    await routerStore.push(query ? linkToQuery(newModeLink) : newModeLink)
  }

  return result.data
}

async function duplicateMode({
  _uid,
  query = false,
  redirect = true,
} = {}) {
  const profileStore = useProfileStore()
  const routerStore = useRouterStore()

  const profile = profileStore[profileKey]

  const {
    mode,
    findMode,
  } = profileStore // current

  const originalMode = (_uid ? findMode(_uid) : mode) || mode
  const newOrder = originalMode.data._order + 1

  const result = await profileQueue.add(
    () => sdk.v3.profiles.modes.copy({
      url: {
        profileUid: profile.uid,
        mUid: (
          _uid || mode.data._uid
        ),
      },
    }),
  ).result

  profileStore[profileKey] = result.data

  // Redirect based on index, to select the newly created mode
  if (redirect) {
    const newModeLink = {
      name: routeNames.Mode,
      params: {
        profileUid: profileStore.profileUid,
        modeUid: newOrder,
      },
    }
    await routerStore.push(query ? linkToQuery(newModeLink) : newModeLink)
  }

  return result.data
}

async function editMode({ _uid, ...payload }) {
  const profileStore = useProfileStore()

  const profile = profileStore[profileKey]

  const {
    mode,
  } = profileStore // current

  const result = await profileQueue.add(
    () => sdk.v3.profiles.modes.edit({
      url: {
        profileUid: profile.uid,
        mUid: (
          _uid || mode.data._uid
        ),
      },
      body: payload,
    }),
  ).result

  profileStore[profileKey] = result.data
  return result.data
}

async function deleteMode({ _uid, query = false } = {}) {
  const profileStore = useProfileStore()
  const routerStore = useRouterStore()

  const profile = profileStore[profileKey]

  const {
    mode,
    modeUid,
  } = profileStore // current

  const redirectTo = getRedirectAfterDelete(mode)
  const toDeleteUid = _uid || modeUid

  const result = await profileQueue.add(
    () => sdk.v3.profiles.modes.delete({
      url: {
        profileUid: profile.uid,
        mUid: toDeleteUid,
      },
    }),
  ).result

  profileStore[profileKey] = result.data

  // If we were looking at the mode being deleted
  if (toDeleteUid === modeUid) {
    // Redirect to closest mode
    await routerStore.push(query ? linkToQuery(redirectTo) : redirectTo)
  }

  return result.data
}

async function moveMode({ _uid, _order }) {
  const profileStore = useProfileStore()

  const profile = profileStore[profileKey]
  const {
    mode,
  } = profileStore // current

  const result = await profileQueue.add(
    () => sdk.v3.profiles.modes.reorder({
      url: {
        profileUid: profile.uid,
        mUid: (
          _uid || mode.data._uid
        ),
      },
      body: { _order },
    }),
  ).result

  profileStore[profileKey] = result.data
  return result.data
}

async function moveLocalChannel({ _uid, _order }) {
  const profileStore = useProfileStore()

  const profile = profileStore[profileKey]
  const {
    mode,
    channel,
  } = profileStore // current

  const result = await profileQueue.add(
    () => sdk.v3.profiles.modes.reorderChannel({
      url: {
        profileUid: profile.uid,
        mUid: mode.data._uid,
        cUid: (
          _uid || channel.data._uid
        ),
      },
      body: { _order },
    }),
  ).result

  profileStore[profileKey] = result.data

  return result.data
}

async function moveGlobalChannel({ _uid, _order }) {
  const profileStore = useProfileStore()

  const profile = profileStore[profileKey]
  const {
    globalChannel,
  } = profileStore // current

  const result = await profileQueue.add(
    () => sdk.v3.profiles.global.reorderChannel({
      url: {
        profileUid: profile.uid,
        cUid: (
          _uid || globalChannel.data._uid
        ),
      },
      body: { _order },
    }),
  ).result

  profileStore[profileKey] = result.data
  return result.data
}

const useModeStore = defineStore('profile/mode', () => {
  const { action: create, status } = useAction(createMode, { meta })

  const visibleModeUid = ref(null)

  return {
    visibleModeUid,
    status,
    create,
    duplicate: useAction(duplicateMode, { status, meta }).action,
    edit: useAction(editMode, { status, meta }).action,
    delete: useAction(deleteMode, { status, meta }).action,
    move: useAction(moveMode, { status, meta }).action,
    moveLocalChannel: useAction(moveLocalChannel, { status, meta }).action,
    moveGlobalChannel: useAction(moveGlobalChannel, { status, meta }).action,
  }
})

export default useModeStore
